import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Schemas } from '../api/client.js';
import { intlLocale } from '../i18n.js';
import { SelectField, TextField } from './ui.js';

export type Address = Schemas['StoredAddress'];

const FIELDS = ['name', 'street', 'postalCode', 'city', 'country', 'vatId'] as const;

/** Two addresses as the fields show them alike: an empty VAT ID is none. */
export const sameAddress = (a: Address, b: Address) =>
  FIELDS.every((key) => (a[key] ?? '') === (b[key] ?? ''));

/**
 * The fields of a billing address (checkout.html «Rechnungsadresse»), with the bounds of the API's
 * address (AddressDto): the customer's order and the operator's invoice take the same one
 * (SP-A2.247). The customer types his own address – the browser may fill it in; the operator
 * types the customer's, so the browser offers none of the operator's own.
 */
export function AddressFields({
  address,
  countries,
  onChange,
  refused,
  vatHint,
  vatNote,
  vatError,
  own,
}: {
  address: Address;
  countries: string[];
  onChange: (address: Address) => void;
  /** What the server refused at a field, if anything. */
  refused: (key: keyof Address) => string | undefined;
  vatHint: string;
  /** A line under the VAT ID: what VIES said of it (checkout.html: .vies). */
  vatNote?: ReactNode;
  vatError?: string | undefined;
  /** The address of the one who types it. */
  own: boolean;
}) {
  const { t, i18n } = useTranslation();
  const names = new Intl.DisplayNames([intlLocale(i18n.language)], { type: 'region' });
  const set = (key: keyof Address) => (e: { target: { value: string } }) =>
    onChange({ ...address, [key]: e.target.value });
  const fill = (token: string) => (own ? token : 'off');
  return (
    <>
      <div className="fields">
        <TextField
          label={t('checkout.name')}
          hint={t('checkout.nameHint')}
          required
          minLength={2}
          maxLength={200}
          autoComplete={fill('organization')}
          value={address.name}
          onChange={set('name')}
          error={refused('name')}
        />
        <TextField
          label={t('checkout.street')}
          required
          minLength={3}
          maxLength={200}
          autoComplete={fill('street-address')}
          value={address.street}
          onChange={set('street')}
          error={refused('street')}
        />
      </div>
      <div className="fields3">
        <TextField
          label={t('checkout.postalCode')}
          required
          minLength={3}
          maxLength={16}
          autoComplete={fill('postal-code')}
          value={address.postalCode}
          onChange={set('postalCode')}
          error={refused('postalCode')}
        />
        <TextField
          label={t('checkout.city')}
          required
          minLength={2}
          maxLength={100}
          autoComplete={fill('address-level2')}
          value={address.city}
          onChange={set('city')}
          error={refused('city')}
        />
        <SelectField
          label={t('checkout.country')}
          value={address.country}
          onChange={set('country')}
          error={refused('country')}
        >
          {countries.map((c) => (
            <option key={c} value={c}>
              {names.of(c) ?? c}
            </option>
          ))}
        </SelectField>
      </div>
      <TextField
        label={t('checkout.vatId')}
        hint={vatHint}
        optional
        maxLength={20}
        placeholder="DE123456789"
        autoComplete={own ? undefined : 'off'}
        value={address.vatId ?? ''}
        onChange={set('vatId')}
        note={vatNote}
        error={vatError}
      />
    </>
  );
}
